import { Autocomplete, Box, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { useVirtualizer } from '@tanstack/react-virtual'
import React, { useRef, useState } from 'react'

import { usePostOrganization } from 'api/aws/organizations_table/usePostOrganization'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import { Organization, OrganizationTableItem } from 'types'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import AddRenderOptionButton from '../Contacts/AddRenderOptionButton'
import CreateDealOrgDialog from './CreateDealOrgDialog'
const renderTypeNames = type => {
  return type === 'Property Management Company'
    ? 'PMC'
    : type === 'Ownership Group'
    ? 'OG'
    : type === 'Agency Whitelabel'
    ? 'AW'
    : type === 'Housing Property'
    ? 'HP'
    : type === 'Parent Client'
    ? 'PC'
    : type === 'Organization'
    ? 'ORG'
    : 'NT'
}

// ─── Virtualized Listbox ──────────────────────────────────────────────────
const ITEM_HEIGHT = 42

const VirtualizedListbox = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function VirtualizedListbox(props, ref) {
    const { children, ...other } = props
    const items = React.Children.toArray(children)
    const parentRef = useRef<HTMLDivElement>(null)

    const virtualizer = useVirtualizer({
      count: items.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => ITEM_HEIGHT,
      overscan: 8,
    })

    return (
      <div ref={ref} {...other}>
        <div
          ref={parentRef}
          style={{
            maxHeight: 300,
            overflow: 'auto',
          }}
        >
          <div
            style={{
              height: virtualizer.getTotalSize(),
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map(virtualRow => (
              <div
                key={virtualRow.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {items[virtualRow.index]}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }
)

// ─── Component ────────────────────────────────────────────────────────────

interface CreateDealOrgAutocompleteProps {
  organizations: OrganizationTableItem[]
  multiple: boolean
  label: string
  value: Organization
  addOrganization: (newValue: any) => Promise<void>
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>
  error: boolean
  openTooltip: boolean
  setOpenTooltip: React.Dispatch<React.SetStateAction<boolean>>
  isDisabled: boolean
  activeContractCountByOrg?: Map<string, number>
}
const CreateDealOrgAutocomplete: React.FC<CreateDealOrgAutocompleteProps> = ({
  organizations,
  multiple = true,
  label,
  value,
  addOrganization,
  setIsValid,
  error,
  openTooltip,
  setOpenTooltip,
  isDisabled = false,
  activeContractCountByOrg,
}) => {
  const theme = useTheme()
  const { showSnackbar } = useSnackbarContext()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [tempInput, setTempInput] = useState('')
  const themeColor = theme.palette.mode === 'dark' ? 'white' : 'black'
  const [selectedOrganizations, setSelectedOrganizations] = useState(Array.isArray(value) ? value : [])
  const { mutate: createOrganization } = usePostOrganization()

  const handleOpenDialog = inputValue => {
    setTempInput(inputValue)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setTempInput('')
  }

  const handleAddOrganization = (event, newOrganization) => {
    if (newOrganization) {
      addOrganization(newOrganization)

      // Check if the selected organization has a valid vertical (not 'No Vertical Assigned')
      const hasValidVertical =
        newOrganization.vertical &&
        newOrganization.vertical.length > 0 &&
        !newOrganization.vertical.includes('No Vertical Assigned')

      setIsValid(hasValidVertical)
      setOpenTooltip(!hasValidVertical) // Show tooltip if vertical is invalid
    } else {
      addOrganization(null)
      setIsValid(false) // Disable buttons if no organization is selected
      setOpenTooltip(false) // Hide tooltip when no organization is selected
    }
  }

  const handleRemoveOrganization = orgId => {
    const newSelectedOrganizations = selectedOrganizations.filter(org => org.org_uuid !== orgId)
    setSelectedOrganizations(newSelectedOrganizations)
  }
  const handleSave = newValue => {
    console.log('[OrgAutocomplete handleSave] newValue: ', newValue)
    createOrganization(newValue, {
      onSuccess: data => {
        console.log('data: ', data)
        const newOrg = { ...data, id: data.org_uuid }
        const updatedOrganizations = [...selectedOrganizations, newOrg]
        setSelectedOrganizations(updatedOrganizations)
        addOrganization(newOrg) // Ensure the newly created organization is selected
        handleCloseDialog()
        showSnackbar('Organization added successfully', 'success')
      },
    })
  }

  const filterOptions = (options, { inputValue }) => {
    let filtered = options.filter(option => (option?.name ?? '').toLowerCase().includes(inputValue.toLowerCase()))

    // Sort alphabetically by name
    filtered.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))

    if (
      inputValue !== '' &&
      !filtered.some(option => (option?.name ?? '').toLowerCase() === inputValue.toLowerCase())
    ) {
      filtered.unshift({
        inputValue,
        specialOption: true,
        id: `add-new-${inputValue}`,
      })
    }

    return filtered
  }

  return (
    <>
      <Tooltip
        title="No vertical assigned to this organization" // Tooltip text
        open={openTooltip} // Control tooltip visibility based on organization validity
        arrow
        placement="top-end" // Adjust placement as needed
      >
        <Autocomplete
          multiple={multiple}
          id="organizations-autocomplete"
          options={organizations ?? []}
          onChange={(event, newValue) => handleAddOrganization(event, newValue)}
          getOptionLabel={option => option.name || ''}
          isOptionEqualToValue={(option, val) => option.org_uuid === val.org_uuid}
          filterOptions={filterOptions}
          value={value || (multiple ? [] : null)}
          ListboxComponent={VirtualizedListbox}
          renderOption={(props, option) => {
            const optionVertical = Array.isArray(option.vertical) ? option.vertical[0] : option.vertical ?? null
            const activeCount = activeContractCountByOrg?.get(option.PK) ?? 0
            return option.specialOption ? (
              <AddRenderOptionButton key={props.id} props={props} option={option} handleOpenDialog={handleOpenDialog} />
            ) : (
              <Box
                component="li"
                {...props}
                sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
                key={option.org_uuid}
              >
                {/* <RenderOptionsWithVertical option={option} /> */}
                <Typography
                  sx={{
                    color: themeColor,
                    fontSize: 14,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    maxWidth: '100%',
                  }}
                  variant="subtitle1"
                >
                  {option && option.name ? option.name : null}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: renderColorByString(optionVertical),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '65%',
                    maxWidth: '65%',
                  }}
                >
                  {optionVertical}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: renderColorByString(option?.type ?? ''),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '15%',
                    maxWidth: '15%',
                  }}
                >
                  {renderTypeNames(option?.type) ?? ''}
                </Typography>
                {activeCount > 0 ? (
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: activeCount > 1 ? 'warning.main' : 'success.main',
                      whiteSpace: 'nowrap',
                      ml: 0.5,
                    }}
                  >
                    {activeCount} MSA
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'text.disabled',
                      whiteSpace: 'nowrap',
                      ml: 0.5,
                    }}
                  >
                    {activeCount} MSA
                  </Typography>
                )}
              </Box>
            )
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map(option => {
              const optionVertical = Array.isArray(option.vertical) ? option.vertical[0] : option.vertical ?? null
              return (
                <Box key={option.org_uuid}>
                  {/* <RenderOptionsWithVertical option={option} /> */}
                  <Typography
                    sx={{
                      color: themeColor,
                      fontSize: 14,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      maxWidth: '100%',
                    }}
                    variant="subtitle1"
                  >
                    {option && option.name ? option.name : null}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: renderColorByString(optionVertical),
                      // overflow: 'hidden',
                      // textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {optionVertical}
                  </Typography>
                </Box>
              )
            })
          }
          renderInput={params => (
            <TextField
              {...params}
              label={isDisabled ? 'Organization' : label}
              error={error} // Set error state
              sx={{
                '& .Mui-disabled': { color: 'text.secondary' },
                '& .MuiInputBase-input.Mui-disabled': {
                  color: theme.palette.text.secondary, // 👈 text color when disabled
                  WebkitTextFillColor: theme.palette.text.secondary, // 👈 required for Safari
                },
                '& .MuiInputBase-root.Mui-disabled': {
                  color: theme.palette.text.secondary, // 👈 icon/button/etc color
                  bgcolor: theme.palette.background.defaultdim,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isDisabled ? theme.palette.text.secondary : undefined, // 👈 optional border color
                },
              }}
            />
          )}
          fullWidth
          disabled={isDisabled}
          // freeSolo
        />
      </Tooltip>
      <CreateDealOrgDialog open={dialogOpen} onClose={handleCloseDialog} onSave={handleSave} tempInput={tempInput} />
    </>
  )
}

export default CreateDealOrgAutocomplete
